// The one place app signals meet the face.
//
// HomePage dispatches events here (WAKE, BOT_TEXT, BOT_SPEAKING, ...). The
// renderer reads from here. Nothing in between is React state.
//
// Two very different rates travel on two different paths:
//
//   - conversation state and emotion change a few times a minute. React
//     subscribes to those via useSyncExternalStore, so a change re-renders the
//     face once.
//
//   - speech amplitude changes every animation frame. It lives in `live`, a plain
//     mutable object that the renderer's rAF loop reads directly. Routing 60
//     updates a second through React state would re-render the whole page every
//     frame on a robot's modest tablet - which is what isTalking already does
//     today via the speaking detector.

import { initialModel, reduce, tick } from "./stateMachine";
import { classify } from "./emotion";

// How long each emotion holds once triggered. Long enough to register from
// across a room, short enough that it does not outlive the sentence that caused
// it by much.
export const EMOTION_MS = {
	happy: 2200,
	excited: 2600,
	delighted: 2800,
	grateful: 2800,
	love: 3200,
	curious: 2600,
	confused: 2800,
	apologetic: 3400,
	surprised: 900,
	shy: 2200,
	wink: 1600,
	smile: 2800,
	sad: 3600,
	cry: 4200,
	frustrated: 3000,
};

// A higher number wins when two emotions race. Negative readings outrank positive
// ones because getting them wrong is costlier: smiling through "sorry, we're out
// of that" reads as mocking the guest.
//
// `surprised` is last on purpose. It is a reflex - the look-up when her name is
// called - not something she means. Any emotion carried by what she actually
// says must be able to replace it, or in a quick exchange her "thank you" face
// would be blocked by her "oh, you called me" face.
export const EMOTION_PRIORITY = {
	cry: 95,
	apologetic: 90,
	sad: 88,
	frustrated: 85,
	confused: 80,
	grateful: 60,
	love: 55,
	delighted: 50,
	excited: 45,
	smile: 42,
	curious: 40,
	shy: 35,
	// A pat on the head is a moment of play; anything she is actually saying
	// should win over it.
	wink: 32,
	happy: 30,
	surprised: 10,
};

// Emotions only make sense on top of some states. Nobody wants a big grin on a
// sleeping face, and the thinking look must stay readable as "working on it".
const EMOTION_STATES = new Set(["idle", "listening", "hearing", "speaking", "waking"]);

export function createFaceStore(clock = () => performance.now()) {
	let model = initialModel(clock());
	let emotion = null; // { name, since, until, reason }
	let override = null; // { state, emotion } - demo/debug only, bypasses the machine
	let snapshot = { state: model.state, since: model.since, emotion: null };
	const subscribers = new Set();

	// Read every frame by the renderer. Mutated in place, never replaced.
	// ampAt is when a real amplitude sample last arrived, so the renderer can
	// tell "quiet" from "no measurement at all". viseme is the measured mouth
	// shape that came with it, if any.
	const live = { amplitude: 0, speaking: false, ampAt: -Infinity, viseme: null };

	const publish = () => {
		const state = override ? override.state : model.state;
		const shown = override
			? override.emotion || null
			: emotion && EMOTION_STATES.has(model.state)
				? emotion.name
				: null;
		if (snapshot.state === state && snapshot.emotion === shown) return;
		snapshot = { state, since: override ? clock() : model.since, emotion: shown };
		subscribers.forEach((fn) => fn());
	};

	// A reply often arrives as several bot-text chunks - "Thank you! Sorry, we're
	// out of paneer tonight." - so emotions race. The rules:
	//   same emotion again ...... extend it (no restart, no flicker)
	//   higher priority ......... replace it now (the apology beats the thanks)
	//   lower priority .......... ignore it while the current one is still showing
	// except when `force`d: the feeling word she opens a reply with ("Oh no,",
	// "Yay!") is her saying exactly how she feels now, so it always wins over an
	// earlier guess.
	function setEmotion(name, now, { reason = "", ms, force = false } = {}) {
		if (!name) return;
		const hold = ms || EMOTION_MS[name] || 2400;
		if (emotion && now < emotion.until && !force) {
			if (name === emotion.name) {
				emotion = { ...emotion, until: Math.max(emotion.until, now + hold) };
				return;
			}
			if ((EMOTION_PRIORITY[name] || 0) < (EMOTION_PRIORITY[emotion.name] || 0)) return;
		}
		emotion = { name, since: now, until: now + hold, reason };
	}

	function dispatch(event) {
		const now = clock();
		const before = model.state;
		model = reduce(model, event, now);

		switch (event.type) {
			case "WAKE":
				// The acknowledgement a guest looks for: she noticed you. A quick
				// widen, like a person looking up when their name is called.
				setEmotion("surprised", now, { reason: "heard her name" });
				break;
			case "BOT_TEXT": {
				const c = classify(event.text);
				if (c) setEmotion(c.emotion, now, { reason: c.reason, force: c.opener });
				break;
			}
			case "CALL_STATUS":
				if (event.status === "connected" && before === "reconnecting") {
					setEmotion("happy", now, { reason: "back online" });
				}
				break;
			case "POKE":
				// BellaBot winks, with a sparkle, when its head is patted.
				setEmotion("wink", now, { reason: "patted" });
				break;
			case "EMOTION":
				// Chosen by a person in the phone app - with a line for her to say, or
				// on its own. A deliberate choice, so it wins over any guess from words.
				setEmotion(event.emotion, now, { reason: event.reason || "chosen in the app", ms: event.ms, force: true });
				break;
			default:
				break;
		}

		if (event.type === "BOT_SPEAKING") live.speaking = !!event.speaking;
		publish();
	}

	// Called by the renderer once per animation frame.
	function frame() {
		const now = clock();
		model = tick(model, now);
		if (emotion && now >= emotion.until) emotion = null;
		// Amplitude decays on its own so the mouth closes even if the detector's
		// last frame was a loud one.
		if (!live.speaking) live.amplitude *= 0.8;
		publish();
		return now;
	}

	return {
		dispatch,
		frame,
		live,
		// `shape` is the mouth shape measured from her voice, { open, wide } (see
		// viseme.js), or null when only loudness is known.
		setAmplitude(level, shape = null) {
			live.amplitude = Math.max(0, Math.min(1, level || 0));
			live.ampAt = clock();
			live.viseme = shape;
		},
		getSnapshot: () => snapshot,
		subscribe(fn) {
			subscribers.add(fn);
			return () => subscribers.delete(fn);
		},
		// Pins the face to a state/emotion regardless of events - for the demo
		// page and for tuning. Pass null to hand control back to the machine.
		setOverride(next) {
			override = next ? { state: next.state || "idle", emotion: next.emotion || null } : null;
			if (override) live.speaking = override.state === "speaking";
			publish();
		},
		// For tests and debugging only.
		_debug: () => ({ model, emotion }),
	};
}

// One face per page.
export const faceStore = createFaceStore();
